import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import PageContent from "../shared/PageContent/PageContent";
import SubNav from "../shared/Navigation/SubNav";
import { useAuth } from "../../context/AuthContext";
import AdminTabTrainingCoursesPage from "./AdminTabTrainingCoursesPage";
import AdminTabIncentivesPage from "./AdminTabIncentivesPage";
import AdminTabProgramsPage from "./AdminTabProgramsPage";
import AdminTabCustomGroupsPage from "./AdminTabCustomGroupsPage";
import AdminTabDocumentsPage from "./AdminTabDocumentsPage";
import AdminTabPayStructurePage from "./AdminTabPayStructurePage";
import OrganizationLevelsTab from "./OrganizationLevelsTab";
import OperatingLevelsTab from "./OperatingLevelsTab";
import "./OrganizationPage.css";

const ADMIN_TABS = [
  "Sales Groups",
  "Operations",
  "Training Courses",
  "Incentives",
  "Programs",
  "Custom Groups",
  "Documents",
  "Pay Structure",
];

const COMPANY_ADMIN_ONLY_TABS = ["Operations", "Documents"];

const MANAGER_ONLY_TABS = [];

const TAB_TO_QUERY = {
  "Sales Groups": "organization",
  Operations: "operations",
  "Training Courses": "courses",
  Incentives: "incentives",
  Programs: "programs",
  "Custom Groups": "groups",
  Documents: "documents",
  "Pay Structure": "paystructure",
};

const QUERY_TO_TAB = {
  organization: "Sales Groups",
  operations: "Operations",
  courses: "Training Courses",
  incentives: "Incentives",
  programs: "Programs",
  groups: "Custom Groups",
  documents: "Documents",
  paystructure: "Pay Structure",
};

const getAdminTabFromSearch = search => {
  const params = new URLSearchParams(search);
  const tabParam = params.get("tab");
  return QUERY_TO_TAB[tabParam] || "Training Courses";
};

const isTabAllowedForRole = (tab, role) => {
  if (COMPANY_ADMIN_ONLY_TABS.includes(tab) && role !== "COMPANY_ADMIN") {
    return false;
  }
  if (MANAGER_ONLY_TABS.includes(tab) && role !== "MANAGER") {
    return false;
  }
  return true;
};

function AdminMainPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() =>
    getAdminTabFromSearch(location.search)
  );

  useEffect(() => {
    const tabFromUrl = getAdminTabFromSearch(location.search);
    const normalizedTab = isTabAllowedForRole(tabFromUrl, user?.role)
      ? tabFromUrl
      : "Training Courses";

    setActiveTab(prevTab =>
      prevTab === normalizedTab ? prevTab : normalizedTab
    );
  }, [location.search, user?.role]);

  useEffect(() => {
    const normalizedActiveTab = isTabAllowedForRole(activeTab, user?.role)
      ? activeTab
      : "Training Courses";

    if (normalizedActiveTab !== activeTab) {
      setActiveTab(normalizedActiveTab);
      return;
    }

    const currentTabParam = new URLSearchParams(location.search).get("tab");
    const nextTabParam = TAB_TO_QUERY[normalizedActiveTab] || "courses";

    if (currentTabParam !== nextTabParam) {
      navigate(
        {
          pathname: location.pathname,
          search: `?tab=${nextTabParam}`,
        },
        { replace: true }
      );
    }
  }, [activeTab, location.pathname, location.search, navigate, user?.role]);

  if (!user || (user.role !== "MANAGER" && user.role !== "COMPANY_ADMIN")) {
    return <Navigate to="/dashboard" replace />;
  }

  const renderActiveTabPage = () => {
    switch (activeTab) {
      case "Sales Groups":
        return <OrganizationLevelsTab />;
      case "Operations":
        return <OperatingLevelsTab />;
      case "Incentives":
        return <AdminTabIncentivesPage />;
      case "Programs":
        return <AdminTabProgramsPage />;
      case "Custom Groups":
        return <AdminTabCustomGroupsPage />;
      case "Documents":
        return <AdminTabDocumentsPage />;
      case "Pay Structure":
        return <AdminTabPayStructurePage />;
      case "Training Courses":
      default:
        return <AdminTabTrainingCoursesPage />;
    }
  };

  return (
    <PageContent
      customHeader={
        <SubNav
          tabs={ADMIN_TABS.filter(tab =>
            isTabAllowedForRole(tab, user.role)
          ).map(tab => ({ id: tab, label: tab }))}
          activeId={activeTab}
          onChange={tab => setActiveTab(tab.id)}
          ariaLabel="Admin sections"
        />
      }
    >
      {renderActiveTabPage()}
    </PageContent>
  );
}

export default AdminMainPage;
